//-tech-debt-react-query;
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deleteItem, fetchItems, postItem } from "../support/queries";
import { addGtmArchitecture, deleteGtmArchitecture, updateGtmArchitecture } from "../support/slices/gtmSolutionsSlice";
import { store } from "../support/store";
import { PartialWithId } from "../types/base-types";
import { CacheKey } from "../types/cache-keys";
import { GtmSolution, GtmSolutionComponent, GtmSolutionUseCase, GtmSolutionUseCaseComponent } from "../types/go-to-market";
import { useAddItemMutation, useDeleteItemMutation, useFetchItem, useFetchItems, useUpdateItemMutation, validId } from "./base-hooks";


// GTM Architecture Solutions
// Currently only interested in architecture solutions for update/delete
const useFetchSingleGtmArchitecture = (id: GtmSolution['id'], params?, options?) => 
  useFetchItem<GtmSolution>(
    'db/go-to-market',
    id,
    () => [CacheKey.GtmSolution, CacheKey.GtmArchitecture],
    { ...params, detailed: true },
    { ...options,
      enabled: typeof options?.enabled === "boolean" 
        ? options.enabled && validId(id) 
        : validId(id)
    }
  );

const useFetchGtmArchitectures = (params?, options?) => 
  useFetchItems<GtmSolution[]>(
    'db/go-to-market',
    () => [CacheKey.GtmSolution, CacheKey.GtmArchitecture],
    { ...params, type: 'architecture', detailed: true },
    options
  );

// Using GtmSolution cache here since we can add non architecture solutions. Base hook handles invalidation of architecture list too
const useAddGtmSolutionMutation = () => 
  useAddItemMutation<GtmSolution>(
    'db/go-to-market',
    () => [CacheKey.GtmSolution],
    {
      onSuccess: (data) => {
        const state = store.getState();
        if (state?.gtmSolutions?.isInitialized && data.type === 'architecture') {
          store.dispatch(addGtmArchitecture(data));
        }
      }
    }
  );

const useUpdateGtmArchitectureMutation = () => 
  useUpdateItemMutation<GtmSolution>(
    'db/go-to-market',
    (payload) => [CacheKey.GtmSolution, CacheKey.GtmArchitecture, payload.id],
    {
      onSuccess: (data) => {
        const state = store.getState();
        if (state?.gtmSolutions?.isInitialized) {
          store.dispatch(updateGtmArchitecture(data));
        }
      }
    }
  );

// using the architecture cache since we only update architecture solutions
const useDeleteGtmArchitectureMutation = () => 
  useDeleteItemMutation<PartialWithId<GtmSolution>>(
    'db/go-to-market',
    (payload) => [CacheKey.GtmSolution, CacheKey.GtmArchitecture, payload.id],
    {
      onSuccess: (_, variables) => {
        const state = store.getState();
        if (state?.gtmSolutions?.isInitialized) {
          store.dispatch(deleteGtmArchitecture(variables.id));
        }
      }
    }
  );


// GTM Solution Component
const useFetchSolutionComponents = (architectureId: GtmSolution['id'], params?, options?) => 
  useFetchItems<GtmSolutionComponent[]>(
    'db/solution-component',
    () => [CacheKey.GtmSolutionComponent, CacheKey.GtmArchitecture, architectureId],
    { ...params, go_to_market: architectureId },
    { ...options,
      enabled: typeof options?.enabled === "boolean" 
        ? options.enabled && validId(architectureId) 
        : validId(architectureId)
    }
  );

const useFetchSingleGtmSolutionComponent = (architectureId: GtmSolution['id'], componentId: GtmSolutionComponent['id'], params?, options?) => 
  useFetchItem<GtmSolutionComponent>(
    'db/solution-component',
    componentId,
    () => [CacheKey.GtmSolutionComponent, CacheKey.GtmArchitecture, architectureId],
    { ...params, detailed: true },
    { ...options,
      enabled: typeof options?.enabled === "boolean" 
        ? options.enabled && validId(architectureId) && validId(componentId)
        : validId(architectureId) && validId(componentId)
    }
  );

const useAddGtmSolutionComponentMutation = (architectureId: GtmSolution['id']) => {
  const queryClient = useQueryClient();

  return useAddItemMutation<GtmSolutionComponent>(
    'db/solution-component',
    () => [CacheKey.GtmSolutionComponent, CacheKey.GtmArchitecture, architectureId],
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [CacheKey.GtmSolution, CacheKey.GtmArchitecture, String(architectureId)] });
      }
    }
  );
};

const useUpdateGtmSolutionComponentMutation = (architectureId: GtmSolution['id']) => {
  const queryClient = useQueryClient();

  return useUpdateItemMutation<GtmSolutionComponent>(
    'db/solution-component',
    (payload) => [CacheKey.GtmSolutionComponent, CacheKey.GtmArchitecture, architectureId, payload.id],
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [CacheKey.GtmSolution, CacheKey.GtmArchitecture, String(architectureId)] });
      }
    },
    { detailed: true }
  );
};

const useDeleteGtmSolutionComponentMutation = (architectureId: GtmSolution['id']) => {
  const queryClient = useQueryClient();

  return useDeleteItemMutation<PartialWithId<GtmSolutionComponent>>(
    'db/solution-component',
    (payload) => [CacheKey.GtmSolutionComponent, CacheKey.GtmArchitecture, architectureId, payload.id],
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [CacheKey.GtmSolution, CacheKey.GtmArchitecture, String(architectureId)] });
      }
    }
  );
};

// GTM Solution Use Case
const useFetchSolutionUseCases = (architectureId: GtmSolution['id'], params?, options?) => 
  useFetchItems(
    'db/security-use-case',
    () => [CacheKey.GtmSolutionUseCase, CacheKey.GtmArchitecture, architectureId],
    { ...params, go_to_market: architectureId },
    { ...options,
      enabled: typeof options?.enabled === "boolean" 
        ? options.enabled && validId(architectureId)
        : validId(architectureId)
    }
  );

const useFetchSingleGtmSolutionUseCase = (architectureId: GtmSolution['id'], useCaseId: GtmSolutionUseCase['id'], params?, options?) => 
  useFetchItem<GtmSolutionUseCase>(
    'db/security-use-case',
    useCaseId,
    () => [CacheKey.GtmSolutionUseCase, CacheKey.GtmArchitecture, architectureId],
    { ...params, detailed: true },
    { ...options,
      enabled: typeof options?.enabled === "boolean" 
        ? options.enabled && validId(architectureId) && validId(useCaseId)
        : validId(architectureId) && validId(useCaseId)
    }
  );

const useAddGtmSolutionUseCaseMutation = (architectureId: GtmSolution['id']) => {
  const queryClient = useQueryClient();

  return useAddItemMutation<GtmSolutionUseCase>(
    'db/security-use-case',
    () => [CacheKey.GtmSolutionUseCase, CacheKey.GtmArchitecture, architectureId],
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [CacheKey.GtmSolution, CacheKey.GtmArchitecture, String(architectureId)] });
      }
    }
  );
};

const useUpdateGtmSolutionUseCaseMutation = (architectureId: GtmSolution['id']) => {
  const queryClient = useQueryClient();

  return useUpdateItemMutation<GtmSolutionUseCase>(
    'db/security-use-case',
    (payload) => [CacheKey.GtmSolutionUseCase, CacheKey.GtmArchitecture, architectureId, payload.id],
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [CacheKey.GtmSolution, CacheKey.GtmArchitecture, String(architectureId)] });
      }
    }
  );
};

const useDeleteGtmSolutionUseCaseMutation = (architectureId: GtmSolution['id']) => {
  const queryClient = useQueryClient();

  return useDeleteItemMutation<PartialWithId<GtmSolutionUseCase>>(
    'db/security-use-case',
    (payload) => [CacheKey.GtmSolutionUseCase, CacheKey.GtmArchitecture, architectureId, payload.id],
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [CacheKey.GtmSolution, CacheKey.GtmArchitecture, String(architectureId)] });
      }
    }
  );
};

// GTM Solution Use Case Component
const useFetchSolutionUseCaseComponents = (useCaseId: GtmSolutionUseCase['id'], params?, options?) => 
  useFetchItems<GtmSolutionUseCaseComponent[]>(
    'db/security-use-case-component',
    () => [CacheKey.GtmSolutionUseCaseComponent, CacheKey.GtmSolutionUseCase, useCaseId],
    { ...params, use_case: useCaseId, detailed: true },
    { ...options,
      enabled: typeof options?.enabled === "boolean" 
        ? options.enabled && validId(useCaseId)
        : validId(useCaseId)
    }
  );

const useFetchSingleGtmSolutionUseCaseComponent = (useCaseId: GtmSolutionUseCase['id'], componentId: GtmSolutionUseCaseComponent['id'], params?, options?) => 
  useFetchItem<GtmSolutionUseCaseComponent>(
    'db/security-use-case-component',
    componentId,
    () => [CacheKey.GtmSolutionUseCaseComponent, CacheKey.GtmSolutionUseCase, useCaseId],
    { ...params, use_case: useCaseId, detailed: true },
    { ...options,
      enabled: typeof options?.enabled === "boolean" 
        ? options.enabled && validId(useCaseId) && validId(componentId)
        : validId(useCaseId) && validId(componentId)
    }
  );

const useAddGtmSolutionUseCaseComponentMutation = (useCaseId: GtmSolutionUseCase['id'], architectureId: GtmSolution['id']) => {
  const queryClient = useQueryClient();

  return useAddItemMutation<GtmSolutionUseCaseComponent>(
    'db/security-use-case-component',
    () => [CacheKey.GtmSolutionUseCaseComponent, CacheKey.GtmSolutionUseCase, useCaseId],
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [CacheKey.GtmSolutionUseCase, CacheKey.GtmArchitecture, String(architectureId)] });
      }
    }
  );
};

const useUpdateGtmSolutionUseCaseComponentMutation = (useCaseId: GtmSolutionUseCase['id'], architectureId: GtmSolution['id']) => {
  const queryClient = useQueryClient();

  return useUpdateItemMutation<GtmSolutionUseCaseComponent>(
    'db/security-use-case-component',
    (payload) => [CacheKey.GtmSolutionUseCaseComponent, CacheKey.GtmSolutionUseCase, useCaseId, payload.id],
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [CacheKey.GtmSolutionUseCase, CacheKey.GtmArchitecture, String(architectureId)] });
      }
    },
    { detailed: true }
  );
};

const useDeleteGtmSolutionUseCaseComponentMutation = (useCaseId: GtmSolutionUseCase['id'], architectureId: GtmSolution['id']) => {
  const queryClient = useQueryClient();

  return useDeleteItemMutation<PartialWithId<GtmSolutionUseCaseComponent>>(
    'db/security-use-case-component',
    (payload) => [CacheKey.GtmSolutionUseCaseComponent, CacheKey.GtmSolutionUseCase, useCaseId, payload.id],
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [CacheKey.GtmSolutionUseCase, CacheKey.GtmArchitecture, String(architectureId)] });
      }
    }
  );
};


interface UseAttachItemsUpdateMutationParams {
  architectureId?: GtmSolution['id'];
  useCaseId?: GtmSolutionUseCase['id'];
  productId?: number;
  type: CacheKey;
}

// Joins
const useAttachedItemsUpdateMutation = <AttachingToT extends { id: number }, AttachingT extends { id: number }>({
  architectureId = null,
  useCaseId = null,
  productId = null,
  type
}: UseAttachItemsUpdateMutationParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      selectedItems: Set<AttachingT['id']>;
      attachingToId: AttachingToT['id'];
    }) => {
      const config = {
        [CacheKey.GtmSolutionComponentFeature]: {
          url: 'db/product-feature-to-solution-component',
          attachingProperty: 'product_feature',
          attachingToProperty: 'solution_component',
          invalidationKeys: [
            [
              CacheKey.GtmSolutionComponent,
              CacheKey.GtmArchitecture,
              ...(architectureId ? [String(architectureId)] : []),
            ],
            [
              CacheKey.GtmSolution,
              CacheKey.GtmArchitecture,
              ...(architectureId ? [String(architectureId)] : []),
            ],
          ],
        },
        [CacheKey.GtmSolutionUseCaseComponentFeature]: {
          url: 'db/security-use-case-component-feature',
          attachingProperty: 'product_feature',
          attachingToProperty: 'security_use_case_component',
          invalidationKeys: [
            [
              CacheKey.GtmSolutionUseCaseComponent,
              CacheKey.GtmSolutionUseCase,
              ...(useCaseId ? [String(useCaseId)] : []),
            ],
            [
              CacheKey.GtmSolutionUseCase,
              CacheKey.GtmArchitecture,
              ...(architectureId ? [String(architectureId)] : []),
              ...(architectureId && useCaseId ? [String(useCaseId)] : []),
            ],
            [
              CacheKey.GtmSolution,
              CacheKey.GtmArchitecture,
              ...(architectureId ? [String(architectureId)] : []),
            ],
          ],
        },
        [CacheKey.ProductComponentFeature]: {
          url: 'db/product-feature-in-product-component',
          attachingProperty: 'product_feature',
          attachingToProperty: 'product_component',
          invalidationKeys: [
            [CacheKey.ProductComponent, CacheKey.Product, ...(productId ? [String(productId)] : [])],
            [CacheKey.CiscoProducts, ...(productId ? [String(productId)] : [])],
          ],
        },
        [CacheKey.ProductFeatureLicense]: {
          url: 'db/product-feature-to-license',
          attachingProperty: 'product_license',
          attachingToProperty: 'product_feature',
          invalidationKeys: [
            [CacheKey.ProductLicense, CacheKey.Product, ...(productId ? [String(productId)] : [])],
            [CacheKey.ProductFeature, CacheKey.Product, ...(productId ? [String(productId)] : [])],
            [CacheKey.CiscoProducts, ...(productId ? [String(productId)] : [])],
          ],
        },
        [CacheKey.ProductLicenseFeature]: {
          // Reverse of above join
          url: 'db/product-feature-to-license',
          attachingProperty: 'product_feature',
          attachingToProperty: 'product_license',
          invalidationKeys: [
            [CacheKey.ProductLicense, CacheKey.Product, ...(productId ? [String(productId)] : [])],
            [CacheKey.ProductFeature, CacheKey.Product, ...(productId ? [String(productId)] : [])],
            [CacheKey.CiscoProducts, ...(productId ? [String(productId)] : [])],
          ],
        },
        [CacheKey.ProductUseCaseFeature]: {
          url: 'db/product-feature-in-product-use-case',
          attachingProperty: 'product_feature',
          attachingToProperty: 'product_use_case',
          invalidationKeys: [
            [CacheKey.ProductUseCase, CacheKey.Product, ...(productId ? [String(productId)] : [])],
            [CacheKey.CiscoProducts, ...(productId ? [String(productId)] : [])],
          ],
        },
      };

      if (!config[type]) {
        throw new Error(`Unsupported type: ${type}`);
      }
      if (!payload.attachingToId) {
        throw new Error('AttachingToId is required');
      }

      const attachingTo = config[type].attachingToProperty;
      const attaching = config[type].attachingProperty;

      const fetchJoins = () => fetchItems(config[type].url, { [attachingTo]: payload.attachingToId });
      const addJoin = (id: number) =>
        postItem(config[type].url, { [attachingTo]: payload.attachingToId, [attaching]: id });
      const deleteJoin = (id: number) => deleteItem(`${config[type].url}/${id}`);

      let refreshNeeded = false;
      try {
        const joinRows = await fetchJoins();
        const joinedItems = new Set(
          (Array.isArray(joinRows) ? joinRows : []).map((join: any) => join[attaching])
        );

        await Promise.all(
          Array.from(payload?.selectedItems || []).map(async (id) => {
            if (!joinedItems.has(id)) {
              refreshNeeded = true;
              return addJoin(id);
            }
          })
        );

        await Promise.all(
          Array.from(joinedItems).map(async (id) => {
            if (!payload.selectedItems.has(id)) {
              const row = joinRows.find((join: any) => join[attaching] === id);
              if (row) {
                refreshNeeded = true;
                return deleteJoin(row.id);
              }
            }
          })
        );
      } catch (error) {
        console.error('Error updating joins:', error);
        throw error;
      } finally {
        if (refreshNeeded) {
          for (const invalidationKey of config[type].invalidationKeys) {
            queryClient.invalidateQueries({ queryKey: invalidationKey });
          }
        }
      }
    },
  })
};

export {
  useFetchSingleGtmArchitecture,
  useFetchGtmArchitectures,
  useAddGtmSolutionMutation,
  useUpdateGtmArchitectureMutation,
  useDeleteGtmArchitectureMutation,

  useFetchSolutionComponents,
  useFetchSingleGtmSolutionComponent,
  useAddGtmSolutionComponentMutation,
  useUpdateGtmSolutionComponentMutation,
  useDeleteGtmSolutionComponentMutation,

  useFetchSolutionUseCases,
  useFetchSingleGtmSolutionUseCase,
  useAddGtmSolutionUseCaseMutation,
  useUpdateGtmSolutionUseCaseMutation,
  useDeleteGtmSolutionUseCaseMutation,

  useFetchSolutionUseCaseComponents,
  useFetchSingleGtmSolutionUseCaseComponent,
  useAddGtmSolutionUseCaseComponentMutation,
  useUpdateGtmSolutionUseCaseComponentMutation,
  useDeleteGtmSolutionUseCaseComponentMutation,

  useAttachedItemsUpdateMutation
};